import React, { useEffect, useState } from "react";
import { Languages } from "lucide-react";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useUiLanguage, useT } from "@/lib/i18n";

/**
 * LanguageSwitcher — header-level picker for BOTH the interface language
 * (what the dashboard text looks like) and the content-generation
 * language (what AI generators output). Phase-1 catalogue: 14 languages
 * grouped into Launch / Phase 1 / Caribbean tiers.
 *
 * UI language is applied immediately via UiLanguageProvider, which
 * auto-translates English UI strings into the target language and caches
 * them in localStorage + MongoDB.
 */
export default function LanguageSwitcher() {
  const [catalog, setCatalog] = useState(null);
  const [contentLang, setContentLang] = useState("en");
  const { lang: uiLang, setLang: setUiLang } = useUiLanguage();
  const t = useT();

  useEffect(() => {
    api.get("/i18n/languages").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/i18n/preference").then((r) => setContentLang(r.data.content_language || "en")).catch(() => {});
  }, []);

  const current = catalog?.all?.find((l) => l.code === uiLang)
              || catalog?.all?.find((l) => l.code === "en");

  const setContent = async (code) => {
    try {
      await api.post("/i18n/preference", { code, scope: "content" });
      setContentLang(code);
      const lang = catalog?.all?.find((l) => l.code === code);
      toast.success(`AI output language: ${lang?.native || code}`);
    } catch {
      toast.error("Couldn't save language preference");
    }
  };

  const setUi = (code) => {
    setUiLang(code);  // provider handles localStorage + backend POST
    const lang = catalog?.all?.find((l) => l.code === code);
    toast.success(`${t("Dashboard language")}: ${lang?.native || code}`);
  };

  if (!catalog) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher"
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-xs hover:text-cyan-300 transition"
          title={t("Switch dashboard + AI output language")}
        >
          <Languages className="h-3.5 w-3.5 text-cyan-300" />
          <span className="hidden sm:inline font-mono uppercase tracking-wider">{current?.code || "EN"}</span>
          <span className="hidden sm:inline text-base leading-none">{current?.flag}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-bg-surface border-line text-ink-primary w-80 max-h-[80vh] overflow-y-auto">
        {/* UI language — the WHOLE dashboard switches */}
        <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-cyan-300">
          // {t("Dashboard language")}
        </DropdownMenuLabel>
        <p className="px-2 pb-1.5 text-[10px] text-ink-muted">
          {t("Translates menus, headers, and buttons on the fly.")}
        </p>

        <LangSection
          label="// LAUNCH"
          items={catalog.launch}
          selected={uiLang}
          onPick={setUi}
          scopeKey="ui"
        />
        <DropdownMenuSeparator className="bg-line my-1" />
        <LangSection
          label="// PHASE 1"
          items={catalog.phase1}
          selected={uiLang}
          onPick={setUi}
          scopeKey="ui"
        />
        <DropdownMenuSeparator className="bg-line my-1" />
        <LangSection
          label="// CARIBBEAN · USP"
          items={catalog.caribbean}
          selected={uiLang}
          onPick={setUi}
          scopeKey="ui"
          highlight
        />

        <DropdownMenuSeparator className="bg-line my-2 h-[2px]" />

        {/* AI output language — what generators produce */}
        <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-brand-amber">
          // {t("AI output language")}
        </DropdownMenuLabel>
        <p className="px-2 pb-1.5 text-[10px] text-ink-muted">
          {t("DJ scripts, news, captions & bios are produced in this language.")}
        </p>
        <LangSection
          label="// LAUNCH"
          items={catalog.launch}
          selected={contentLang}
          onPick={setContent}
          scopeKey="content"
        />
        <DropdownMenuSeparator className="bg-line my-1" />
        <LangSection
          label="// PHASE 1"
          items={catalog.phase1}
          selected={contentLang}
          onPick={setContent}
          scopeKey="content"
        />
        <DropdownMenuSeparator className="bg-line my-1" />
        <LangSection
          label="// CARIBBEAN · USP"
          items={catalog.caribbean}
          selected={contentLang}
          onPick={setContent}
          scopeKey="content"
          highlight
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LangSection({ label, items, selected, onPick, highlight, scopeKey }) {
  return (
    <>
      <DropdownMenuLabel className={`text-[10px] font-mono uppercase tracking-wider ${highlight ? "text-cyan-300" : "text-ink-muted"}`}>
        {label}
      </DropdownMenuLabel>
      {items.map((l) => (
        <DropdownMenuItem
          key={l.code}
          onClick={() => onPick(l.code)}
          data-testid={`lang-${scopeKey}-option-${l.code}`}
          className="cursor-pointer hover:text-cyan-300 focus:text-cyan-300 flex items-center gap-2"
        >
          <span className="text-base">{l.flag}</span>
          <span className="flex-1">
            <span className="text-sm">{l.name}</span>
            <span className="text-[10px] text-ink-muted ml-2">{l.native}</span>
          </span>
          {selected === l.code && <span className="text-cyan-300 font-mono text-[10px]">ACTIVE</span>}
        </DropdownMenuItem>
      ))}
    </>
  );
}
